import './Dashboard.css'; // CSS file

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, logout } from '../utils/api';

function Dashboard() {
    const [uploads, setUploads] = useState([]);
    const [user, setUser] = useState(null);
    const [time, setTime] = useState(""); // State for digital clock
    const [showCleanedDatasets, setShowCleanedDatasets] = useState(true); // Filter state
    const navigate = useNavigate();

    // Separate original and cleaned datasets
    // Handle both integer (1/0) and boolean/null values
    const originalDatasets = uploads.filter(upload => {
        const isCleaned = upload.is_cleaned;
        return isCleaned === 0 || isCleaned === false || isCleaned === null || isCleaned === undefined;
    });
    const cleanedDatasets = uploads.filter(upload => {
        const isCleaned = upload.is_cleaned;
        return isCleaned === 1 || isCleaned === true;
    });

    const fetchData = async () => {
        try {
            const data = await apiRequest('/api/dashboard');
            if (data.status === 'success') {
                setUser(data.user);
                // Debug: Log is_cleaned values
                console.log('Dashboard data received:', data.data.map(u => ({
                    name: u.file_name,
                    is_cleaned: u.is_cleaned,
                    type: typeof u.is_cleaned
                })));
                setUploads(data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (error.message === 'Unauthorized') {
                navigate('/login');
            }
        }
    };

    useEffect(() => {
        // Fetch data
        fetchData();

        // Start the clock
        const interval = setInterval(() => {
            const currentTime = new Date();
            const hours = currentTime.getHours() % 12 || 12; // Convert to 12-hour format
            const minutes = currentTime.getMinutes().toString().padStart(2, "0");
            const seconds = currentTime.getSeconds().toString().padStart(2, "0");
            const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
            setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
        }, 1000);

        return () => clearInterval(interval); // Cleanup interval on unmount
    }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return 'N/A';
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        const mb = kb / 1024;
        return `${mb.toFixed(2)} MB`;
    };

    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            // Still redirect to login even if logout fails
            navigate('/login');
        }
    };

    const handleStartAutoML = () => {
        navigate('/analysis-selection'); // Navigate to Analysis Selection first
    };

    const handleDeleteDataset = async (fileId, fileName) => {
        // Confirm deletion
        const confirmed = window.confirm(
            `Are you sure you want to delete "${fileName}"?\n\n` +
            `This will permanently delete the file and cannot be undone.` +
            (fileName.includes('_cleaned') ? '' : '\n\nNote: Any cleaned datasets created from this file will also be deleted.')
        );
        
        if (!confirmed) {
            return;
        }
        
        try {
            const response = await apiRequest(`/api/dashboard/dataset/${fileId}`, {
                method: 'DELETE'
            });
            
            if (response.status === 'success') {
                // Refresh the data
                await fetchData();
            } else {
                alert('Failed to delete dataset: ' + (response.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error deleting dataset:', error);
            alert('Failed to delete dataset: ' + (error.message || 'Unknown error'));
        }
    };

    return (
        <div className="dashboard">
            {/* Top Navigation Bar */}
            <div className="top-nav">
                <div className="nav-content">
                    <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                        <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
                    </div>
                    <div className="nav-right">
                        <div className="digital-clock">{time}</div>
                <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                </div>
            </div>

            {/* Welcome Section - Compact */}
            <div className="welcome-section">
                <div className="welcome-content">
                    <div>
                        <h1>Welcome back, {user ? user.name : "Loading..."}</h1>
                        <p className="welcome-subtitle">Healthcare Data Analysis Workspace</p>
                        <p className="welcome-date">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <button onClick={handleStartAutoML} className="quick-action-button">
                        Start Analysis
                    </button>
                </div>
            </div>

            {/* Main Content - Compact Grid */}
            <div className="dashboard-content">
                {/* Stats Row */}
                <div className="stats-row">
                    <div className="stat-card">
                        <div className="stat-icon stat-icon--text">DS</div>
                        <div className="stat-content">
                            <div className="stat-value">{uploads.length}</div>
                            <div className="stat-label">Total Datasets</div>
                            <div className="stat-desc">All uploaded files</div>
                        </div>
                    </div>
                    <div className="stat-card stat-card--cleaned">
                        <div className="stat-icon stat-icon--text">CL</div>
                        <div className="stat-content">
                            <div className="stat-value">{cleanedDatasets.length}</div>
                            <div className="stat-label">Cleaned Datasets</div>
                            <div className="stat-desc">Ready for analysis</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon stat-icon--text">SZ</div>
                        <div className="stat-content">
                            <div className="stat-value">{formatFileSize(uploads.reduce((sum, u) => sum + (u.file_size || 0), 0))}</div>
                            <div className="stat-label">Total Data Size</div>
                            <div className="stat-desc">{uploads.length} file{uploads.length !== 1 ? 's' : ''} combined</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon stat-icon--text">UP</div>
                        <div className="stat-content">
                            <div className="stat-value">{uploads.length > 0 ? formatRelativeTime(uploads[0].upload_date) : 'N/A'}</div>
                            <div className="stat-label">Last Upload</div>
                            <div className="stat-desc">{uploads.length > 0 ? uploads[0].file_name.length > 20 ? uploads[0].file_name.substring(0, 20) + '...' : uploads[0].file_name : 'No files yet'}</div>
                        </div>
                    </div>
                </div>

                {/* Datasets Sections - Split into Two Equal Sections */}
                <div className="datasets-sections-container">
                    {/* Original Datasets Section */}
                    <div className="section datasets-section">
                        <div className="section-header">
                            <h2>Original Datasets ({originalDatasets.length})</h2>
                            <button onClick={handleStartAutoML} className="upload-plus-button" title="Upload new dataset">+</button>
                        </div>
                        {originalDatasets.length > 0 ? (
                            <div className="datasets-table">
                                <div className="table-header">
                                    <span className="col-name">File Name</span>
                                    <span className="col-size">Size</span>
                                    <span className="col-date">Uploaded</span>
                                    <span className="col-action">Action</span>
                                </div>
                                <div className="table-body">
                                    {originalDatasets.slice(0, 8).map((upload, index) => {
                                        const fileSize = formatFileSize(upload.file_size);
                                        return (
                                            <div key={index} className="table-row">
                                                <span className="col-name" title={upload.file_name}>
                                                    {upload.file_name.length > 30 
                                                        ? upload.file_name.substring(0, 30) + '...' 
                                                        : upload.file_name}
                                                </span>
                                                <span className="col-size">{fileSize}</span>
                                                <span className="col-date">{formatRelativeTime(upload.upload_date)}</span>
                                                <div className="col-action">
                                                    <a 
                                                        href={upload.download_link} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="table-link"
                                                    >
                                                        Download
                                                    </a>
                                                    <button
                                                        onClick={() => handleDeleteDataset(upload.file_id, upload.file_name)}
                                                        className="delete-button"
                                                        title="Delete dataset"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {originalDatasets.length > 8 && (
                                    <div className="table-footer">
                                        <span>Showing 8 of {originalDatasets.length} datasets</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <p className="empty-message">No datasets uploaded yet</p>
                            </div>
                        )}
                    </div>

                    {/* Cleaned Datasets Section */}
                    <div className="section cleaned-datasets-section">
                        <div className="section-header">
                            <h2>Cleaned Datasets ({cleanedDatasets.length})</h2>
                            <label className="filter-toggle">
                                <input
                                    type="checkbox"
                                    checked={showCleanedDatasets}
                                    onChange={(e) => setShowCleanedDatasets(e.target.checked)}
                                />
                                <span className="toggle-switch"></span>
                                <span className="toggle-label">Show</span>
                            </label>
                        </div>
                        {showCleanedDatasets ? (
                            cleanedDatasets.length > 0 ? (
                                <div className="datasets-table">
                                    <div className="table-header">
                                        <span className="col-name">File Name</span>
                                        <span className="col-size">Size</span>
                                        <span className="col-method">Method</span>
                                        <span className="col-date">Created</span>
                                        <span className="col-action">Action</span>
                                    </div>
                                    <div className="table-body">
                                        {cleanedDatasets.slice(0, 8).map((upload, index) => {
                                            const fileSize = formatFileSize(upload.file_size);
                                            let methodDisplay = 'Custom';
                                            try {
                                                const methodJson = upload.cleaning_method ? JSON.parse(upload.cleaning_method) : null;
                                                if (methodJson && typeof methodJson === 'object') {
                                                    const methods = Object.values(methodJson);
                                                    const uniqueMethods = [...new Set(methods)];
                                                    if (uniqueMethods.length === 1) {
                                                        methodDisplay = uniqueMethods[0].charAt(0).toUpperCase() + uniqueMethods[0].slice(1);
                                                    } else {
                                                        methodDisplay = `${uniqueMethods.length} methods`;
                                                    }
                                                }
                                            } catch (e) {
                                                // If not JSON, treat as old format
                                                methodDisplay = upload.cleaning_method || 'Unknown';
                                            }
                                            return (
                                                <div key={index} className="table-row">
                                                    <span className="col-name" title={upload.file_name}>
                                                        {upload.file_name.length > 25 
                                                            ? upload.file_name.substring(0, 25) + '...' 
                                                            : upload.file_name}
                                                    </span>
                                                    <span className="col-size">{fileSize}</span>
                                                    <span className="col-method">
                                                        <span className="method-badge">{methodDisplay}</span>
                                                    </span>
                                                    <span className="col-date">{formatRelativeTime(upload.upload_date)}</span>
                                                    <div className="col-action">
                                                        <a 
                                                            href={upload.download_link} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="table-link"
                                                        >
                                                            Download
                                                        </a>
                                                        <button
                                                            onClick={() => handleDeleteDataset(upload.file_id, upload.file_name)}
                                                            className="delete-button"
                                                            title="Delete dataset"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {cleanedDatasets.length > 8 && (
                                        <div className="table-footer">
                                            <span>Showing 8 of {cleanedDatasets.length} cleaned datasets</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p className="empty-message">No cleaned datasets yet</p>
                                    <p className="empty-submessage">Clean a dataset from the Dataset Preview page to see it here</p>
                                </div>
                            )
                        ) : (
                            <div className="empty-state">
                                <p className="empty-message">Cleaned datasets are hidden</p>
                                <p className="empty-submessage">Check the "Show" toggle above to display them</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Analysis Workflow - Full Width Illustration */}
                <div className="section workflow-section">
                    <h2>Analysis Workflow</h2>
                    <div className="workflow-pipeline">
                        <div className="workflow-pipeline-step">
                            <div className="workflow-pipeline-num">1</div>
                            <div className="workflow-pipeline-icon wf-icon-choose"></div>
                            <div className="workflow-pipeline-label">Choose Analysis</div>
                            <div className="workflow-pipeline-desc">Select supervised or unsupervised learning</div>
                        </div>
                        <div className="workflow-pipeline-arrow">&rsaquo;</div>
                        <div className="workflow-pipeline-step">
                            <div className="workflow-pipeline-num">2</div>
                            <div className="workflow-pipeline-icon wf-icon-upload"></div>
                            <div className="workflow-pipeline-label">Upload</div>
                            <div className="workflow-pipeline-desc">Import your CSV healthcare dataset</div>
                        </div>
                        <div className="workflow-pipeline-arrow">&rsaquo;</div>
                        <div className="workflow-pipeline-step">
                            <div className="workflow-pipeline-num">3</div>
                            <div className="workflow-pipeline-icon wf-icon-preview"></div>
                            <div className="workflow-pipeline-label">Dataset Preview</div>
                            <div className="workflow-pipeline-desc">Inspect columns and clean missing values</div>
                        </div>
                        <div className="workflow-pipeline-arrow">&rsaquo;</div>
                        <div className="workflow-pipeline-step">
                            <div className="workflow-pipeline-num">4</div>
                            <div className="workflow-pipeline-icon wf-icon-algorithms"></div>
                            <div className="workflow-pipeline-label">Select Algorithms</div>
                            <div className="workflow-pipeline-desc">Choose the ML models to train and compare</div>
                        </div>
                        <div className="workflow-pipeline-arrow">&rsaquo;</div>
                        <div className="workflow-pipeline-step">
                            <div className="workflow-pipeline-num">5</div>
                            <div className="workflow-pipeline-icon wf-icon-visualize"></div>
                            <div className="workflow-pipeline-label">Visualize</div>
                            <div className="workflow-pipeline-desc">Review charts, metrics, and model performance</div>
                        </div>
                        <div className="workflow-pipeline-arrow">&rsaquo;</div>
                        <div className="workflow-pipeline-step">
                            <div className="workflow-pipeline-num">6</div>
                            <div className="workflow-pipeline-icon wf-icon-predict"></div>
                            <div className="workflow-pipeline-label">Predict</div>
                            <div className="workflow-pipeline-desc">Apply the trained model to new data</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
